import React, { FC, Suspense, useContext, useEffect, useRef, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import ReportData from "go-report/reports";
import { getChartReport, getLastMonthFilterBetween, getReport } from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import ReportPaymentsUtils from "../../../../../../../../utils/reports/ReportPaymentsUtils";
import { ReportTransactionsUtils } from "../../../../../../../../utils/reports/ReportTransactionsUtils";

interface ListState {
	resource?: Record<string, any>;
}

const type = "transaction";
const listName = "TRANSACTION_REPORT";
const resourceType = "TRANSACTION_REPORT";

const Report: FC<ListState> = ({ resource }): JSX.Element | null => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const segmentContext = useContext(SegmentContext);
	const filtersParams = useRef<string>("");

	if (!resource) return null;

	const data = resource.read();
	const columnsFromLocalStorage = ReportPaymentsUtils.getSelectedColumns("transactions");
	let config = {
		reportConfigId: "transactions",
		type: "transaction",
		defaultGroup: "NONE",
		aggregatePrefix: "sales",
		chart: ReportTransactionsUtils.getChartInfo(t),
		groups: ReportTransactionsUtils.getGroups(t),
		groupsInfo: ReportTransactionsUtils.getGroupsInfo(t),
		selectedColumns: columnsFromLocalStorage,
		showPercent: true,
		columns: ReportTransactionsUtils.getColumns(t),
		filters: ReportTransactionsUtils.getFilters(t, filtersParams, type),
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: columnsFromLocalStorage,
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
				],
			},
		],
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			filtersParams.current = params.f;
			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					organization.id,
					config.filters,
					config.type,
					sourceToken
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"NONE",
					organization.id,
					config.filters,
					config.type,
					sourceToken
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"NONE",
				organization.id,
				config.filters,
				config.type,
				sourceToken
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.transaction.field.export_config.report.title"),
			filename: t("modules.transaction.field.export_config_filename.title"),
			organization: organization.name,
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		withCompareDateRange: true,
	} as ReportConfig;
	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

export const OrganizationReportsTransactionPage = (): JSX.Element => {
	const [resource, setResource] = useState<Record<string, any>>();
	const segmentContext = useContext(SegmentContext);

	useEffect(() => {
		setResource(wrapPromise(segmentContext.get(listName, resourceType)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} />
		</Suspense>
	);
};
